import { Bell, ChevronsUpDown, LogOut, SettingsIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/shadcn/avatar.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu.tsx"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/shadcn/sidebar.tsx"
import useModal from "@/features/modals/hooks/use-modal.ts"
import { MODALS } from "@/features/modals/types/modals.tsx"
import { NOTIFICATIONS_LIST_VIEW } from "@/features/notifications/routes/route-list.ts"
import { useGetProfileQuery } from "@/features/settings-account/api/account-api.ts"
import { SETTINGS_LIST_VIEW } from "@/features/settings-account/routes/route-list.ts"
import { logout } from "@/redux/slices/auth-slice.ts"
import { type AppDispatch, persistor } from "@/redux/store.ts"

export function NavUser() {
  const { isMobile } = useSidebar()
  const { data } = useGetProfileQuery()
  const dispatch = useDispatch<AppDispatch>()
  const { openModal } = useModal()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const signOut = () => {
    dispatch(logout())
      .unwrap()
      .then(() => persistor.purge())
  }

  const handleSignOutClick = () => {
    openModal(MODALS.BASE_MODAL, {
      title: t("signOutConfirmation"),
      onActionClick: signOut,
    })
  }

  const navigateToNotifications = () => {
    navigate(NOTIFICATIONS_LIST_VIEW)
  }

  const navigateToSettings = () => {
    navigate(SETTINGS_LIST_VIEW)
  }

  const nameAbbreviation =
    data &&
    data?.first_name.split(" ")[0].slice(0, 1) + data?.last_name.slice(0, 1)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={data?.picture} alt={data?.first_name} />
                <AvatarFallback className="rounded-lg">
                  {nameAbbreviation}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {data?.first_name}
                </span>
                <span className="truncate text-xs">{data?.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={data?.picture} alt={data?.first_name} />
                  <AvatarFallback className="rounded-lg">CN</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {data?.first_name}
                  </span>
                  <span className="truncate text-xs">{data?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup className={"cursor-pointer"}>
              <DropdownMenuItem onClick={navigateToSettings}>
                <SettingsIcon />
                {t("settings")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={navigateToNotifications}>
                <Bell />
                {t("notifications")}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleSignOutClick}
              className="hover:bg-red-500"
            >
              <LogOut />
              {t("signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
